import * as React from "react";
import { invariant } from "@/util/invariant.ts";

export interface LogEntry {
  readonly cv_id: string;
  readonly name: string | null;
  readonly user_id: string;
}

interface ContextType {
  log: readonly LogEntry[];
  logUpdatedCVs: (updated_cvs: readonly LogEntry[]) => void;
  clearActivityLogs: () => void;
}

const Context = React.createContext<ContextType | null>(null);

export const ActivityLogsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [log, setLog] = React.useState<readonly LogEntry[]>([]);

  const value = React.useMemo<ContextType>(() => {
    function logUpdatedCVs(updated_cvs: readonly LogEntry[]) {
      if (updated_cvs.length === 0) {
        return;
      }

      setLog((log) => [...log, ...updated_cvs]);
    }

    function clearActivityLogs() {
      setLog((log) => {
        if (log.length === 0) {
          return log;
        }

        return [];
      });
    }

    return { log, logUpdatedCVs, clearActivityLogs };
  }, [log]);

  return <Context value={value}>{children}</Context>;
};

function useActivityLogsContext() {
  const context = React.use(Context);

  invariant(context, "useActivityLogsContext must be used within context");

  return context;
}

export function useActivityLogs() {
  const { log } = useActivityLogsContext();

  return log;
}

export function useLogUpdatedCVs() {
  const { logUpdatedCVs } = useActivityLogsContext();

  return logUpdatedCVs;
}

export function useClearActivityLogs() {
  const { clearActivityLogs } = useActivityLogsContext();

  return clearActivityLogs;
}
